import React, { useState, useEffect } from 'react';
import { projectsAPI, seasonsAPI, teamtoolsAPI, teamsAPI } from '../../services/api';
import PortalMultiSelectDropdown from '../common/PortalMultiSelectDropdown';

const ProjectsSettings = () => {
  const [projects, setProjects] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projectTeams, setProjectTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sid: '',
    published: false
  });
  const [assignData, setAssignData] = useState({
    selectedTeams: [],
    startPoints: 0
  });
  useEffect(() => {
    fetchProjects();
    fetchSeasons();
    fetchTeams();
  }, []);
  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
      // Fetch teams for each project
      await fetchProjectTeams(response.data);
    } catch (err) {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasons = async () => {
    try {
      const response = await seasonsAPI.getAll();
      setSeasons(response.data);
    } catch (err) {
      console.error('Failed to fetch seasons');
    }
  };  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams');
    }
  };

  // Get available teams for assignment (not already assigned to the selected project)
  const getAvailableTeams = () => {
    if (!selectedProject || !projectTeams[selectedProject._id]) {
      return teams;
    }
    
    const assignedTeamIds = projectTeams[selectedProject._id].map(pt => pt.tid?._id || pt.tid);
    return teams.filter(team => !assignedTeamIds.includes(team._id));
  };

  const fetchProjectTeams = async (projectsList = projects) => {
    try {
      const teamsData = {};
      await Promise.all(
        projectsList.map(async (project) => {
          const response = await teamtoolsAPI.getByProject(project._id);
          teamsData[project._id] = response.data;
        })
      );
      setProjectTeams(teamsData);
    } catch (err) {
      console.error('Failed to fetch project teams');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject._id, formData);
      } else {
        await projectsAPI.create(formData);
      }
      fetchProjects();
      resetForm();
    } catch (err) {
      setError('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsAPI.delete(id);
        fetchProjects();
      } catch (err) {
        setError('Failed to delete project');
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({ name: '', sid: '', published: false });
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      sid: project.sid?._id || '',
      published: project.published
    });
    setShowModal(true);
  };
  const openAssignModal = (project) => {
    setSelectedProject(project);
    setAssignData({
      selectedTeams: [],
      startPoints: 0
    });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    
    if (assignData.selectedTeams.length === 0) {
      setError('Please select at least one team');
      return;
    }

    try {
      setUploading(true);
      
      // Create teamtool entries for each selected team
      const promises = assignData.selectedTeams.map(teamId => 
        teamtoolsAPI.create({
          pid: selectedProject._id,
          tid: teamId,
          start: assignData.startPoints
        })
      );
      
      await Promise.all(promises);
      
      setShowAssignModal(false);
      setAssignData({
        selectedTeams: [],
        startPoints: 0
      });
      
      // Refresh project teams
      await fetchProjectTeams();
      
      // Show success message
      alert(`Successfully assigned ${assignData.selectedTeams.length} teams to ${selectedProject.name}!`);
      
    } catch (err) {
      console.error('Error assigning teams:', err);
      setError('Failed to assign teams to project');
    } finally {
      setUploading(false);
    }
  };

  // Function to remove a team from a project
  const handleRemoveTeam = async (teamtoolId, teamName, projectName) => {
    if (!window.confirm(`Remove ${teamName} from ${projectName}?`)) {
      return;
    }
    
    try {
      setUploading(true);
      await teamtoolsAPI.delete(teamtoolId);
      
      // Refresh project teams
      await fetchProjectTeams();
      
      alert(`Successfully removed ${teamName} from ${projectName}!`);
    } catch (err) {
      console.error('Error removing team:', err);
      setError('Failed to remove team assignment');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Projects</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          Add Project
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>        </div>      )}

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Season</th>
              <th>Teams</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(projects) && projects.map((project) => (
              <tr key={project._id}>
                <td>{project.name}</td>
                <td>
                  {project.sid ? (
                    <div className="badge badge-outline">
                      {project.sid.name || 'Season Assigned'}
                    </div>                  ) : (
                    <span className="text-gray-500">No Season</span>
                  )}
                </td>                <td>
                  {projectTeams[project._id] && projectTeams[project._id].length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {projectTeams[project._id].slice(0, 2).map((teamtool) => (
                        <div key={teamtool._id} className="badge badge-info badge-sm">
                          {teamtool.tid?.name || 'Team'}
                          {teamtool.start !== 0 && ` (${teamtool.start > 0 ? '+' : ''}${teamtool.start})`}
                        </div>
                      ))}
                      {projectTeams[project._id].length > 2 && (
                        <button 
                          className="badge badge-ghost badge-sm cursor-pointer hover:badge-primary transition-colors"
                          onClick={() => openAssignModal(project)}
                        >
                          +{projectTeams[project._id].length - 2} more
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">No teams assigned</span>
                  )}
                </td>
                <td>
                  <div className={`badge ${project.published ? 'badge-success' : 'badge-error'}`}>
                    {project.published ? 'Published' : 'Draft'}
                  </div>                </td>
                <td>
                  <div className="flex gap-1">
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleEdit(project)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => openAssignModal(project)}
                    >
                      Teams
                    </button>
                    <button 
                      className="btn btn-sm btn-error"
                      onClick={() => handleDelete(project._id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.isArray(projects) && projects.map((project) => (
          <div key={project._id} className="modern-card p-6">
            {/* Project Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                {project.name}
              </h3>
              <div className={`badge ${project.published ? 'badge-success' : 'badge-error'}`}>
                {project.published ? 'Published' : 'Draft'}
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-3 mb-4">
              <div>
                <span className="text-gray-500 text-sm block mb-1">Season:</span>
                {project.sid ? (
                  <div className="badge badge-outline">
                    {project.sid.name || 'Season Assigned'}
                  </div>
                ) : (
                  <span className="text-gray-500">No Season</span>
                )}
              </div>

              <div>
                <span className="text-gray-500 text-sm block mb-2">Teams:</span>
                {projectTeams[project._id] && projectTeams[project._id].length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {projectTeams[project._id].slice(0, 3).map((teamtool) => (
                      <div key={teamtool._id} className="badge badge-info badge-sm">
                        {teamtool.tid?.name || 'Team'}
                        {teamtool.start !== 0 && ` (${teamtool.start > 0 ? '+' : ''}${teamtool.start})`}
                      </div>
                    ))}
                    {projectTeams[project._id].length > 3 && (
                      <button 
                        className="badge badge-ghost badge-sm cursor-pointer hover:badge-primary transition-colors"
                        onClick={() => openAssignModal(project)}
                      >
                        +{projectTeams[project._id].length - 3} more
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">No teams assigned</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button 
                className="btn btn-sm btn-primary flex-1"
                onClick={() => handleEdit(project)}
              >
                Edit
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => openAssignModal(project)}
              >
                Teams
              </button>
              <button 
                className="btn btn-sm btn-error"
                onClick={() => handleDelete(project._id)}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {editingProject ? 'Edit Project' : 'Add New Project'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input input-bordered"
                  required
                />
              </div>              <div className="form-control">
                <label className="label">
                  <span className="label-text">Season</span>
                </label>
                <select
                  value={formData.sid}
                  onChange={(e) => setFormData({...formData, sid: e.target.value})}
                  className="select select-bordered"
                >
                  <option value="">Select Season</option>
                  {Array.isArray(seasons) && seasons.map((season) => (
                    <option key={season._id} value={season._id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Published</span>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>
              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingProject ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}      {/* Assign Teams Modal - Enhanced Sexy Version */}
      {showAssignModal && selectedProject && (
        <dialog open className="modal" style={{ zIndex: 9998 }}>
          <div className="modal-box max-w-5xl bg-gradient-to-br from-white via-blue-50 to-indigo-100 dark:from-gray-800 dark:via-blue-900 dark:to-indigo-900 border-2 border-gradient shadow-2xl">
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-xl">
              <h3 className="font-bold text-xl text-white flex items-center gap-3 m-0">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⚽</span>
                </div>
                Team Management
                <div className="ml-auto text-sm bg-white/20 px-3 py-1 rounded-full">
                  {selectedProject.name}
                </div>
              </h3>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar">
              {/* Currently Assigned Teams Section */}
              <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {projectTeams[selectedProject._id]?.length || 0}
                  </div>
                  <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                    Currently Assigned Teams
                  </span>
                </h4>
                {projectTeams[selectedProject._id]?.length > 0 && (
                  <div className="badge badge-success gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Active
                  </div>
                )}
              </div>
              
              {projectTeams[selectedProject._id] && projectTeams[selectedProject._id].length > 0 ? (
                <div className="glass-morphism p-6 rounded-2xl max-h-64 overflow-y-auto custom-scrollbar">
                  <div className="grid gap-3">
                    {projectTeams[selectedProject._id].map((teamtool, index) => (
                      <div 
                        key={teamtool._id} 
                        className="group bg-white/80 backdrop-blur-sm hover:bg-white/90 p-4 rounded-xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1"
                        style={{
                          animationDelay: `${index * 0.1}s`,
                          animation: 'fadeInUp 0.6s ease-out forwards'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                              <span className="text-white text-lg font-bold">
                                {(teamtool.tid?.name || 'T').charAt(0)}
                              </span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-800 text-lg">
                                {teamtool.tid?.name || 'Team'}
                              </span>
                              {teamtool.start !== 0 && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm text-gray-600">Starting points:</span>
                                  <div className={`badge ${teamtool.start > 0 ? 'badge-success' : 'badge-warning'} gap-1`}>
                                    {teamtool.start > 0 ? '📈' : '📉'}
                                    {teamtool.start > 0 ? '+' : ''}{teamtool.start}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            className="btn btn-error btn-sm group-hover:btn-outline transition-all duration-300 hover:scale-110"
                            onClick={() => handleRemoveTeam(
                              teamtool._id, 
                              teamtool.tid?.name || 'Team', 
                              selectedProject.name
                            )}
                            disabled={uploading}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="glass-morphism p-8 rounded-2xl text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🏟️</span>
                  </div>
                  <p className="text-gray-500 text-lg">No teams assigned to this project</p>
                  <p className="text-gray-400 text-sm mt-1">Add some teams below to get started</p>
                </div>
              )}
            </div>

            {/* Add New Teams Section */}
            <div className="border-t-2 border-gradient pt-6">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">+</span>
                </div>
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Add New Teams
                </span>
              </h4>

              <form id="assign-teams-form" onSubmit={handleAssignSubmit} className="space-y-6">
                <div className="glass-morphism p-6 rounded-2xl">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium text-gray-700 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Select Teams
                      </span>
                    </label>
                    <PortalMultiSelectDropdown
                      options={getAvailableTeams()}
                      selected={assignData.selectedTeams}
                      onChange={(selectedIds) => setAssignData({...assignData, selectedTeams: selectedIds})}
                      placeholder="Choose teams to assign..."
                      displayKey="name"
                      searchKey="name"
                    />
                    {assignData.selectedTeams.length > 0 && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 text-green-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">
                            {assignData.selectedTeams.length} team{assignData.selectedTeams.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="glass-morphism p-6 rounded-2xl">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium text-gray-700 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Starting Points
                      </span>
                    </label>
                    <input
                      type="number"
                      value={assignData.startPoints}
                      onChange={(e) => setAssignData({
                        ...assignData,
                        startPoints: e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                      })}
                      className="input input-bordered input-lg bg-white/80 backdrop-blur-sm border-2 focus:border-purple-400 transition-all duration-300"
                      min="-50"
                      max="50"
                      placeholder="0"
                    />
                    <label className="label">
                      <span className="label-text-alt text-gray-500 flex items-center gap-1">
                        0 = normal start, negative values = penalty points
                      </span>
                    </label>
                  </div>
                </div>
              </form>
            </div>
            </div>

            {/* Sticky footer — always visible */}
            <div className="modal-action flex-shrink-0 gap-3 px-6 py-4 border-t border-gray-200 bg-white/95">
              <button
                type="submit"
                form="assign-teams-form"
                className="btn btn-primary btn-lg flex-1 bg-gradient-to-r from-blue-600 to-purple-600 border-0 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
                disabled={uploading || assignData.selectedTeams.length === 0}
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="loading loading-spinner loading-sm"></span>
                    Assigning...
                  </span>
                ) : (
                  <span>
                    Assign {assignData.selectedTeams.length > 0 ? `${assignData.selectedTeams.length} ` : ''}Team{assignData.selectedTeams.length !== 1 ? 's' : ''}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-lg"
                onClick={() => setShowAssignModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default ProjectsSettings;
